import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { User } from "stream-chat";
import { CustomMessageActions, MessageList, useChannelActionContext, useChannelStateContext } from "stream-chat-react";
import { useTheme } from "../../../hooks/useTheme";
import { useAuth } from "../../../hooks/useAuth";
import { createChatChannelModel } from "../../../utils/ChatChannelUtils";
import { ChannelExtended } from "./ChatListTile";
import { FriendshipStatusType } from "../../../models/FriendshipStatus";
import { useRouter } from "../../../hooks/useRouter";
import { ChatChannelType } from "../../../models/ChatChannel";
import PinnedMessageBannerContainer from "./PinnedMessageBannerContainer";
import { useBlockedUserIds } from "../../../hooks/useBlockedUsers";
import { isMessageFromBlockedUser } from "../../../utils/BlockedMessageUtils";

const messageActions = [
    'edit',
    'delete',
    'quote',
    'react',
    'reply',
    'pin'
];

const CustomMessageList = () => {
    const navigate = useNavigate();
    const [showButton, setShowButton] = useState(false);
    const { isMobile } = useTheme();
    const {tab} = useParams();
    const { channel, messages } = useChannelStateContext();
    const { pleb } = useAuth();
    const { navigateToProfile } = useRouter();
    const { jumpToMessage } = useChannelActionContext('CustomMessageList');
    const [searchParams] = useSearchParams();
    const queryJumpToMessage = searchParams.get('jumpToMessage');

    useEffect(() => {
        const element = document.querySelector('.str-chat__list');

        const handleScroll = () => {
            if (element) {
                const tolerance = 100;
                const atBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + tolerance;
                setShowButton(!atBottom);
            }
        };

        if (element) {
            element.addEventListener('scroll', handleScroll);
        }

        // Cleanup
        return () => {
            if (element) {
                element.removeEventListener('scroll', handleScroll);
            }
        };
    }, []);

    useEffect(() => {
        const chatChannel = createChatChannelModel(channel as ChannelExtended, pleb?.id ?? '');

        if(chatChannel.friendshipStatus.type === FriendshipStatusType.RequestSent && 
            chatChannel.friendshipStatus.isBlocked) {
            navigateToProfile(chatChannel.type ?? ChatChannelType.Messaging, chatChannel.id);
        }
    }, [channel]);

    useEffect(() => {
        if(queryJumpToMessage) {
            jumpToMessage(queryJumpToMessage);
        }
    }, [queryJumpToMessage, jumpToMessage]);

    const blockedUserIds = useBlockedUserIds();

    // Filtered before the list so date separators and message grouping skip hidden messages
    const visibleMessages = useMemo(() => {
        if (!messages || blockedUserIds.size === 0 || channel?.type === ChatChannelType.Messaging) {
            return messages;
        }
        return messages.filter((message) =>
            !isMessageFromBlockedUser(message, channel?.type, pleb?.id, blockedUserIds));
    }, [messages, blockedUserIds, channel?.type, pleb?.id]);

    const scrollToBottom = () => {
        const element = document.querySelector('.str-chat__list');
        if (element) {
          element.scrollTo({ top: element.scrollHeight, behavior: 'smooth' });
        }
      };
    
    const handleUserClick = (event: React.BaseSyntheticEvent, user: User) => {
        navigate(`/home/${tab}/user/${user.id}`);
    }

    return <>
        <PinnedMessageBannerContainer />
        <MessageList 
            messages={visibleMessages}
            messageActions={messageActions}
            onUserClick={handleUserClick}
            returnAllReadData={true}
            additionalMessageInputProps={{
                maxRows: 10
            }}
        />
        {showButton && (<button
                onClick={scrollToBottom}
                className={
                    `absolute bottom-[20px] right-[20px] w-10 h-10 rounded-full flex items-center justify-center
                    bg-brand-bg-1 text-brand-orange border border-brand-border-color
                    hover:bg-gray-50 dark:hover:bg-gray-700
                    transition duration-300
                    `
                }
            >
                <span className="material-symbols-outlined text-[34px] pt-1">
                    keyboard_arrow_down
                </span>
            </button>)}
    </>
}

export default CustomMessageList;